import React, { useState } from 'react';
import { useNovelUpdate } from '../hooks/useNovelUpdate';
import './StatesCategory.css';
import CustomSelect from './CustomSelect';

const StatesCategory = ({ novelId, novel, setNovel, refreshNovel, isProduction = false }) => {
  const [selectedStateType, setSelectedStateType] = useState('flags');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingState, setEditingState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use states from novel prop instead of local state
  const flags = novel?.states?.flags || [];
  const scales = novel?.states?.scales || [];

  const stateTypes = [
    { key: 'flags', label: 'Флаги', icon: '🏳️' },
    { key: 'scales', label: 'Шкалы', icon: '📊' }
  ];

  const { updateNovel } = useNovelUpdate(novelId, isProduction, refreshNovel);

  const handleCreateState = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    let initialValue = formData.get('initial_value');

    if (!name) {
      setError('Название состояния обязательно');
      setLoading(false);
      return;
    }

    if (initialValue === null || initialValue === '') {
      setError('Начальное значение обязательно');
      setLoading(false);
      return;
    }

    try {
      let newState;
      if (selectedStateType === 'flags') {
        newState = {
          name,
          initial_value: initialValue === 'true'
        };
      } else {
        const numValue = parseInt(initialValue);
        if (isNaN(numValue)) {
          setError('Начальное значение должно быть числом');
          setLoading(false);
          return;
        }
        newState = {
          name,
          initial_value: numValue
        };
      }

      const currentStates = selectedStateType === 'flags' ? flags : scales;
      const updatedStates = [...currentStates, newState];

      const updatedNovel = {
        ...novel,
        states: {
          ...novel.states,
          [selectedStateType]: updatedStates
        }
      };

      await updateNovel(updatedNovel);
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditState = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    const formData = new FormData(event.target);
    const name = formData.get('name')?.trim();
    let initialValue = formData.get('initial_value');

    if (!name) {
      setError('Название состояния обязательно');
      setLoading(false);
      return;
    }

    if (initialValue === null || initialValue === '') {
      setError('Начальное значение обязательно');
      setLoading(false);
      return;
    }

    try {
      let updatedState;
      if (selectedStateType === 'flags') {
        updatedState = {
          ...editingState,
          name,
          initial_value: initialValue === 'true'
        };
      } else {
        const numValue = parseInt(initialValue);
        if (isNaN(numValue)) {
          setError('Начальное значение должно быть числом');
          setLoading(false);
          return;
        }
        updatedState = {
          ...editingState,
          name,
          initial_value: numValue
        };
      }

      const currentStates = selectedStateType === 'flags' ? flags : scales;
      const updatedStates = currentStates.map(state =>
        state.id === editingState.id ? updatedState : state
      );

      const updatedNovel = {
        ...novel,
        states: {
          ...novel.states,
          [selectedStateType]: updatedStates
        }
      };

      await updateNovel(updatedNovel);
      setEditingState(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteState = async (stateId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это состояние?')) {
      return;
    }

    try {
      setLoading(true);
      const currentStates = selectedStateType === 'flags' ? flags : scales;
      const updatedStates = currentStates.filter(state => state.id !== stateId);

      const updatedNovel = {
        ...novel,
        states: {
          ...novel.states,
          [selectedStateType]: updatedStates
        }
      };

      await updateNovel(updatedNovel);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStates = () => {
    return selectedStateType === 'flags' ? flags : scales;
  };

  const getAddButtonText = () => {
    return selectedStateType === 'flags' ? 'Добавить флаг' : 'Добавить шкалу';
  };

  const getFormTitle = () => {
    if (editingState) {
      return selectedStateType === 'flags' ? 'Редактировать флаг' : 'Редактировать шкалу';
    }
    return selectedStateType === 'flags' ? 'Добавить флаг' : 'Добавить шкалу';
  };

  const formatValue = (value) => {
    if (selectedStateType === 'flags') {
      return value ? 'Истина' : 'Ложь';
    }
    return value.toString();
  };

  return (
    <div className="sc-wrap">
      {/* Description */}
      <div className="sc-desc">
        <p>{'Состояния контролируют ветвление игры и поведение персонажей. Флаги — булевы значения, шкалы — числовые.'}</p>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="sc-overlay" onClick={() => setShowCreateForm(false)}>
          <div className="sc-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sc-modal-title">{getFormTitle()}</h3>
            {error && <div className="sc-error">{error}</div>}
            <form onSubmit={handleCreateState}>
              <div className="sc-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название состояния"
                  required
                  disabled={loading}
                  className="sc-input"
                />
              </div>

              <div className="sc-form-group">
                {selectedStateType === 'flags' ? (
                  <CustomSelect
                    name="initial_value"
                    required
                    disabled={loading}
                    className="app-select"
                    defaultValue=""
                  >
                    <option value="" disabled>{'Начальное значение'}</option>
                    <option value="true">{'Истина'}</option>
                    <option value="false">{'Ложь'}</option>
                  </CustomSelect>
                ) : (
                  <input
                    type="number"
                    name="initial_value"
                    placeholder="Начальное значение"
                    required
                    disabled={loading}
                    className="sc-input"
                    step="1"
                  />
                )}
              </div>

              <div className="sc-modal-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="sc-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Создать'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  disabled={loading}
                  className="sc-btn-cancel"
                >
                  {'Отменить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingState && (
        <div className="sc-overlay" onClick={() => setEditingState(null)}>
          <div className="sc-modal" onClick={e => e.stopPropagation()}>
            <h3 className="sc-modal-title">{getFormTitle()}</h3>
            {error && <div className="sc-error">{error}</div>}
            <form onSubmit={handleEditState}>
              <div className="sc-form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Название состояния"
                  defaultValue={editingState.name}
                  required
                  disabled={loading}
                  className="sc-input"
                />
              </div>

              <div className="sc-form-group">
                {selectedStateType === 'flags' ? (
                  <CustomSelect
                    name="initial_value"
                    required
                    disabled={loading}
                    className="app-select"
                    defaultValue={editingState.initial_value ? 'true' : 'false'}
                  >
                    <option value="true">{'Истина'}</option>
                    <option value="false">{'Ложь'}</option>
                  </CustomSelect>
                ) : (
                  <input
                    type="number"
                    name="initial_value"
                    placeholder="Начальное значение"
                    defaultValue={editingState.initial_value}
                    required
                    disabled={loading}
                    className="sc-input"
                    step="1"
                  />
                )}
              </div>

              <div className="sc-modal-actions">
                <button
                  type="submit"
                  disabled={loading}
                  className="sc-btn-submit"
                >
                  {loading ? 'Загрузка...' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingState(null)}
                  disabled={loading}
                  className="sc-btn-cancel"
                >
                  {'Отменить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* State Type Tabs */}
      <div className="sc-tabs">
        {stateTypes.map((type) => (
          <button
            key={type.key}
            className={`sc-tab ${selectedStateType === type.key ? 'active' : ''}`}
            onClick={() => setSelectedStateType(type.key)}
          >
            <span className="tab-icon">{type.icon}</span>
            <span className="tab-label">{type.label}</span>
            <span className="tab-count">({type.key === 'flags' ? flags.length : scales.length})</span>
          </button>
        ))}
      </div>

      {/* Controls */}
      <button
        className="sc-add-btn"
        onClick={() => setShowCreateForm(true)}
      >
        <span>➕</span>
        {getAddButtonText()}
      </button>

      {/* States List */}
      {getCurrentStates().length > 0 ? (
        <div className="sc-list">
          {getCurrentStates().map((state) => (
            <div key={state.id} className="sc-card">
              <h4 className="sc-card-name">{state.name}</h4>
              <span className={`sc-card-value ${selectedStateType === 'flags' ? (state.initial_value ? 'true' : 'false') : 'number'}`}>
                {formatValue(state.initial_value)}
              </span>
              <div className="sc-card-actions">
                <button
                  className="sc-btn-edit"
                  onClick={() => setEditingState(state)}
                  disabled={loading}
                >
                  {'Редактировать'}
                </button>
                <button
                  className="sc-btn-delete"
                  onClick={() => handleDeleteState(state.id)}
                  disabled={loading}
                >
                  {'Удалить'}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="sc-empty">
          <p>{selectedStateType === 'flags' ? 'Флагов пока нет. Создайте свой первый флаг!' : 'Шкал пока нет. Создайте свою первую шкалу!'}</p>
        </div>
      )}
    </div>
  );
};

export default StatesCategory;
